import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  LinearProgress,
  Fade,
  Zoom,
  Button
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Lightbulb as LightbulbIcon,
  WbSunny as WbSunnyIcon,
  Nightlight as NightlightIcon,
  AcUnit as AcUnitIcon,
  Opacity as OpacityIcon,
  Thermostat as ThermostatIcon,
  Air as AirIcon,
  WaterDrop as WaterDropIcon,
  CalendarToday as CalendarIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import moment from 'moment-timezone';
import axiosInstance from './axiosConfig';
import { useTemperatureUnit } from './TemperatureUnitContext';
import ModernBackground from './common/ModernBackground';
import { useBranding } from './common/BrandingSystem';
import { useLanguage } from '../contexts/LanguageContext';

const RoomLandingPage = () => {
  const { roomId } = useParams();
  const [room, setRoom] = useState(null);
  const [lastValues, setLastValues] = useState({ temperature: 0, humidity: 0, co2: 0, soil_moisture: null });
  const [roomConfig, setRoomConfig] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(moment().format('HH:mm:ss'));
  const [daysSinceStart, setDaysSinceStart] = useState(0);
  const [sensorStatus, setSensorStatus] = useState('online');
  const [openSettings, setOpenSettings] = useState(false);
  const [settingsPassword, setSettingsPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [harvestDate, setHarvestDate] = useState(null);

  const { temperatureUnit, convertTemperature } = useTemperatureUnit() || { 
    temperatureUnit: 'Celsius', 
    convertTemperature: (value) => value.toFixed(2) 
  };
  const { branding } = useBranding() || { companyName: 'HarvestAI' };
  const { translate } = useLanguage() || { translate: (key) => key };

  const cultivationDuration = 70; // 10 weeks
  const refreshInterval = 30000; // 30 seconds

  // Fetch room data
  const fetchRoomData = async () => {
    try {
      const [roomResponse, configResponse, latestResponse] = await Promise.all([
        axiosInstance.get(`/api/rooms/${roomId}`),
        axiosInstance.get(`/api/rooms/${roomId}/config`),
        axiosInstance.get(`/api/rooms/${roomId}/latest`)
      ]);

      setRoom(roomResponse.data);
      setRoomConfig(configResponse.data.roomConfig);

      if (latestResponse.data.message && latestResponse.data.message.includes("No se encontraron mediciones recientes")) {
        setSensorStatus('offline');
        setLastValues({ temperature: 0, humidity: 0, co2: 0, soil_moisture: null });
      } else {
        setSensorStatus('online');
        setLastValues(latestResponse.data);
      }

      setLastUpdate(moment().format('HH:mm:ss'));
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching room data:', error);
      setSensorStatus('offline');
      setIsLoading(false);
    }
  };

  // Calculate cultivation progress
  useEffect(() => {
    if (roomConfig?.cultivation_start_date) {
      const startDate = moment(roomConfig.cultivation_start_date);
      const today = moment();
      const daysElapsed = today.diff(startDate, 'days');
      setDaysSinceStart(daysElapsed);
      
      // Calculate harvest date
      const harvestDate = startDate.add(cultivationDuration, 'days');
      setHarvestDate(harvestDate);
    }
  }, [roomConfig]);

  // Auto-refresh data
  useEffect(() => {
    if (roomId) {
      fetchRoomData();
      const interval = setInterval(fetchRoomData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [roomId]);

  // Check if lights are on
  const isLightOn = () => {
    if (!roomConfig || !roomConfig.light_schedule_start || !roomConfig.light_schedule_end) return false;
    const now = moment().tz('America/Los_Angeles');
    const start = moment.tz(roomConfig.light_schedule_start, 'HH:mm', 'America/Los_Angeles');
    const end = moment.tz(roomConfig.light_schedule_end, 'HH:mm', 'America/Los_Angeles');
    return now.isBetween(start, end) || (end.isBefore(start) && (now.isAfter(start) || now.isBefore(end)));
  };

  // Get color based on sensor values
  const getColor = (type, value) => {
    switch (type) {
      case 'temperature':
        return value < 18 ? '#2196F3' : value <= 29 ? '#4CAF50' : value <= 30 ? '#FF9800' : '#F44336';
      case 'humidity':
        return value < 30 ? '#2196F3' : value <= 50 ? '#4CAF50' : value <= 60 ? '#FF9800' : '#F44336';
      case 'co2':
        return value < 800 ? '#2196F3' : value <= 1500 ? '#4CAF50' : value <= 1800 ? '#FF9800' : '#F44336';
      case 'soil_moisture':
        return value < 40 ? '#2196F3' : value <= 70 ? '#4CAF50' : value <= 80 ? '#FF9800' : '#F44336';
      default:
        return '#757575';
    }
  };

  // Handle settings access
  const handleSettingsAccess = () => {
    if (settingsPassword === 'admin123') { // This should come from room config
      setOpenSettings(true);
      setSettingsPassword('');
      setPasswordError(false);
    } else {
      setPasswordError(true);
      setTimeout(() => setPasswordError(false), 2000);
    }
  };

  // Save room settings
  const handleSaveSettings = async (newSettings) => {
    try {
      await axiosInstance.put(`/api/rooms/${roomId}/config`, newSettings);
      setOpenSettings(false);
      fetchRoomData(); // Refresh data
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  const displayTemperature = convertTemperature(lastValues.temperature);
  const temperatureLabel = temperatureUnit === 'Fahrenheit' ? '°F' : '°C';
  const progressPercentage = Math.min((daysSinceStart / cultivationDuration) * 100, 100);

  if (isLoading) {
    return (
      <ModernBackground>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column'
        }}>
          <CircularProgressbar value={0} text="Cargando..." />
          <Typography variant="h6" sx={{ mt: 2 }}>Inicializando Room Display</Typography>
        </Box>
      </ModernBackground>
    );
  }

  return (
    <ModernBackground>
      <Box sx={{ 
        minHeight: '100vh',
        p: 3,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 4,
          p: 2,
          borderRadius: 3,
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <Box>
            <Typography variant="h2" sx={{ fontWeight: 700, color: 'white' }}>
              {room?.name || translate('roomLandingPage.room')}
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {branding.companyName} - Sistema de Monitoreo
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              icon={sensorStatus === 'online' ? <CheckCircleIcon /> : <ErrorIcon />}
              label={sensorStatus === 'online' ? 'Online' : 'Offline'}
              color={sensorStatus === 'online' ? 'success' : 'error'}
              sx={{ background: 'rgba(255, 255, 255, 0.9)' }}
            />
            <Typography variant="body2" sx={{ color: 'white' }}>
              {translate('roomLandingPage.lastUpdate')}: {lastUpdate}
            </Typography>
            <IconButton 
              onClick={fetchRoomData}
              sx={{ color: 'white' }}
            >
              <RefreshIcon />
            </IconButton>
            <IconButton 
              onClick={() => setOpenSettings(true)}
              sx={{ color: 'white' }}
            >
              <SettingsIcon />
            </IconButton>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Sensor Values */}
          <Grid item xs={12} md={8}>
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: 3
            }}>
              <CardContent>
                <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                  {translate('roomLandingPage.currentConditions')}
                </Typography>
                
                {sensorStatus === 'offline' ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <WarningIcon sx={{ fontSize: 60, color: '#f39c12', mb: 2 }} />
                    <Typography variant="h5" color="warning.main" gutterBottom>
                      {translate('roomLandingPage.sensorsOffline')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {translate('roomLandingPage.noRecentMeasurements')}
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={6} sm={3}>
                      <Zoom in={true} style={{ transitionDelay: '0ms' }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <CircularProgressbar
                            value={displayTemperature}
                            maxValue={30}
                            text={`${displayTemperature}${temperatureLabel}`}
                            styles={buildStyles({
                              textColor: '#333',
                              pathColor: getColor('temperature', lastValues.temperature),
                              trailColor: '#e0e0e0',
                            })}
                          />
                          <Typography variant="h6" sx={{ mt: 1 }}>{translate('roomLandingPage.temperature')}</Typography>
                        </Box>
                      </Zoom>
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                      <Zoom in={true} style={{ transitionDelay: '100ms' }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <CircularProgressbar
                            value={lastValues.humidity}
                            maxValue={60}
                            text={`${lastValues.humidity}%`}
                            styles={buildStyles({
                              textColor: '#333',
                              pathColor: getColor('humidity', lastValues.humidity),
                              trailColor: '#e0e0e0',
                            })}
                          />
                          <Typography variant="h6" sx={{ mt: 1 }}>{translate('roomLandingPage.humidity')}</Typography>
                        </Box>
                      </Zoom>
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                      <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <CircularProgressbar
                            value={lastValues.co2}
                            maxValue={1800}
                            text={`${lastValues.co2} ppm`}
                            styles={buildStyles({
                              textColor: '#333',
                              pathColor: getColor('co2', lastValues.co2),
                              trailColor: '#e0e0e0',
                            })}
                          />
                          <Typography variant="h6" sx={{ mt: 1 }}>{translate('roomLandingPage.co2')}</Typography>
                        </Box>
                      </Zoom>
                    </Grid>
                    
                    {lastValues.soil_moisture !== null && (
                      <Grid item xs={6} sm={3}>
                        <Zoom in={true} style={{ transitionDelay: '300ms' }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <CircularProgressbar
                              value={lastValues.soil_moisture}
                              maxValue={80}
                              text={`${lastValues.soil_moisture}%`}
                              styles={buildStyles({
                                textColor: '#333',
                                pathColor: getColor('soil_moisture', lastValues.soil_moisture),
                                trailColor: '#e0e0e0',
                              })}
                            />
                            <Typography variant="h6" sx={{ mt: 1 }}>{translate('roomLandingPage.soilMoisture')}</Typography>
                          </Box>
                        </Zoom>
                      </Grid>
                    )}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Equipment Status */}
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: 3,
              height: 'fit-content'
            }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  {translate('roomLandingPage.equipmentStatus')}
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Lights */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {isLightOn() ? <WbSunnyIcon color="warning" /> : <NightlightIcon color="primary" />}
                    <LightbulbIcon color={isLightOn() ? 'warning' : 'disabled'} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1">{translate('roomLandingPage.lighting')}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {roomConfig?.light_schedule_start && roomConfig?.light_schedule_end
                          ? `${roomConfig.light_schedule_start} - ${roomConfig.light_schedule_end}`
                          : translate('roomLandingPage.noConfiguration')}
                      </Typography>
                    </Box>
                    <Chip 
                      label={isLightOn() ? translate('roomLandingPage.on') : translate('roomLandingPage.off')} 
                      color={isLightOn() ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  {/* Air Conditioner */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AcUnitIcon color={roomConfig?.air_conditioner?.status === 'on' ? 'primary' : 'disabled'} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1">{translate('roomLandingPage.airConditioner')}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {translate('roomLandingPage.max')}: {roomConfig?.air_conditioner?.threshold || translate('roomLandingPage.na')}°C
                      </Typography>
                    </Box>
                    <Chip 
                      label={roomConfig?.air_conditioner?.status === 'on' ? translate('roomLandingPage.on') : translate('roomLandingPage.off')} 
                      color={roomConfig?.air_conditioner?.status === 'on' ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  {/* Humidifier */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <OpacityIcon color={roomConfig?.humidifier?.status === 'on' ? 'primary' : 'disabled'} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1">{translate('roomLandingPage.humidifier')}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {translate('roomLandingPage.min')}: {roomConfig?.humidifier?.threshold || translate('roomLandingPage.na')}
                      </Typography>
                    </Box>
                    <Chip 
                      label={roomConfig?.humidifier?.status === 'on' ? translate('roomLandingPage.on') : translate('roomLandingPage.off')} 
                      color={roomConfig?.humidifier?.status === 'on' ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  {/* Dehumidifier */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WaterDropIcon color={roomConfig?.dehumidifier?.status === 'on' ? 'primary' : 'disabled'} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1">{translate('roomLandingPage.dehumidifier')}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {translate('roomLandingPage.max')}: {roomConfig?.dehumidifier?.threshold || translate('roomLandingPage.na')}
                      </Typography>
                    </Box>
                    <Chip 
                      label={roomConfig?.dehumidifier?.status === 'on' ? translate('roomLandingPage.on') : translate('roomLandingPage.off')} 
                      color={roomConfig?.dehumidifier?.status === 'on' ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Cultivation Progress */}
          <Grid item xs={12}>
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: 3
            }}>
              <CardContent>
                <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                  {translate('roomLandingPage.cultivationProgress')}
                </Typography>
                
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h1" sx={{ fontSize: '4rem', mb: 1 }}>
                        {daysSinceStart >= cultivationDuration - 3 ? '🌾' : 
                         daysSinceStart >= (cultivationDuration * 0.75) ? '🌳' : 
                         daysSinceStart >= (cultivationDuration * 0.5) ? '🌿' : '🌱'}
                      </Typography>
                      <Typography variant="h5" gutterBottom>
                        {translate('roomLandingPage.day')} {daysSinceStart} {translate('roomLandingPage.of')} {cultivationDuration}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {daysSinceStart >= cultivationDuration - 3 ? translate('roomLandingPage.readyToHarvest') :
                         daysSinceStart >= (cultivationDuration * 0.75) ? translate('roomLandingPage.floweringPhase') :
                         daysSinceStart >= (cultivationDuration * 0.5) ? translate('roomLandingPage.growthPhase') : translate('roomLandingPage.germinationPhase')}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1">{translate('roomLandingPage.progress')}</Typography>
                        <Typography variant="body1">{Math.floor(progressPercentage)}%</Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={progressPercentage} 
                        sx={{ 
                          height: 20, 
                          borderRadius: 10,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 10,
                            background: 'linear-gradient(90deg, #4CAF50, #8BC34A)'
                          }
                        }} 
                      />
                      
                      {harvestDate && (
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                          <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          <Typography variant="body1">
                            {translate('roomLandingPage.harvestDate')}: {harvestDate.format('DD/MM/YYYY')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {harvestDate.diff(moment(), 'days')} {translate('roomLandingPage.daysRemaining')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Settings Modal */}
        <Dialog 
          open={openSettings} 
          onClose={() => setOpenSettings(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: 'white',
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 700,
            fontSize: '1.3rem',
          }}>
            {translate('roomLandingPage.roomConfiguration')} - {room?.name}
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <RoomSettingsModal 
              roomConfig={roomConfig}
              onSave={handleSaveSettings}
              onClose={() => setOpenSettings(false)}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </ModernBackground>
  );
};

// Settings Modal Component
const RoomSettingsModal = ({ roomConfig, onSave, onClose }) => {
  const { translate } = useLanguage();
  const [settings, setSettings] = useState({
    light_schedule_start: '',
    light_schedule_end: '',
    cultivation_start_date: '',
    air_conditioner: { threshold: '', status: 'off' },
    humidifier: { threshold: '', status: 'off' },
    dehumidifier: { threshold: '', status: 'off' }
  });

  useEffect(() => {
    if (roomConfig) {
      setSettings(roomConfig);
    }
  }, [roomConfig]);

  const handleSave = () => {
    onSave(settings);
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.4)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(0, 0, 0, 0.7)',
    },
    '& .MuiInputBase-input': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label={translate('roomLandingPage.maxTemperature')}
            type="number"
            fullWidth
            value={settings.air_conditioner?.threshold || ''}
            onChange={(e) => setSettings({
              ...settings, 
              air_conditioner: {...settings.air_conditioner, threshold: e.target.value}
            })}
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={translate('roomLandingPage.minHumidity')}
            type="number"
            fullWidth
            value={settings.humidifier?.threshold || ''}
            onChange={(e) => setSettings({
              ...settings, 
              humidifier: {...settings.humidifier, threshold: e.target.value}
            })}
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={translate('roomLandingPage.maxHumidity')}
            type="number"
            fullWidth
            value={settings.dehumidifier?.threshold || ''}
            onChange={(e) => setSettings({
              ...settings, 
              dehumidifier: {...settings.dehumidifier, threshold: e.target.value}
            })}
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={translate('roomLandingPage.cultivationStartDate')}
            type="date"
            fullWidth
            value={settings.cultivation_start_date}
            onChange={(e) => setSettings({...settings, cultivation_start_date: e.target.value})}
            InputLabelProps={{ shrink: true }}
            sx={textFieldSx}
          />
        </Grid>
        
        {/* Equipment Settings */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
            {translate('roomLandingPage.equipmentConfiguration')}
          </Typography>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            label={translate('roomLandingPage.lightScheduleStart')}
            type="time"
            fullWidth
            value={settings.light_schedule_start}
            onChange={(e) => setSettings({...settings, light_schedule_start: e.target.value})}
            InputLabelProps={{ shrink: true }}
            sx={textFieldSx}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            label={translate('roomLandingPage.lightScheduleEnd')}
            type="time"
            fullWidth
            value={settings.light_schedule_end}
            onChange={(e) => setSettings({...settings, light_schedule_end: e.target.value})}
            InputLabelProps={{ shrink: true }}
            sx={textFieldSx}
          />
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button 
          onClick={onClose}
          sx={{ 
            textTransform: 'none',
            color: 'rgba(0, 0, 0, 0.7)',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          {translate('roomLandingPage.cancel')}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          sx={{
            textTransform: 'none',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
            },
          }}
        >
          {translate('roomLandingPage.saveConfiguration')}
        </Button>
      </Box>
    </Box>
  );
};

export default RoomLandingPage; 